import type { ShiftBreaksList } from '@libs/data-access/queries'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { usePadSettings } from '@spa/scenes/Plannings/hooks'
import classnames from 'classnames'
import React from 'react'
import type { FC } from 'react'

import { displayRulesLegacy, getBreaksInfosLegacy } from '../../../../utils'

import styles from './BreakCol.module.css'

const BREAK_EVENT = 'break_event'

type Props = {
  breaks: ShiftBreaksList
  size: number
  type: 'planned' | 'real'
  cardType?: 'planned' | 'real' | 'pad'
  shiftIsFinishedWithoutOut: boolean
  hasEmployeeModifyReal?: boolean
  isShiftValid?: boolean
  hasSnapPadBreakEnable?: boolean
}

/**
 * @deprecated file
 */

export const BreakColLegacy: FC<Props> = ({
  breaks,
  size,
  type = 'planned',
  cardType = 'planned',
  shiftIsFinishedWithoutOut,
  hasEmployeeModifyReal = false,
  isShiftValid = false,
  hasSnapPadBreakEnable = false
}) => {
  const { t, tCommon, tDates } = usePlanningFormTranslation()

  const { hasSnapPad, padType, hasSnapPadBreaksEnable, breakAsReal } =
    usePadSettings()

  const noFinishedBreaks =
    ((cardType === 'pad' && hasSnapPad && hasSnapPadBreaksEnable) ||
      cardType === 'real') &&
    breaks?.size === 0

  /* If no break is taken then we display a single line */
  if (noFinishedBreaks) {
    return (
      <div className={styles.BreaksRow}>
        <span>{t('shiftRealHoursForm.breakCol.break', { count: size })}</span>
        <span className={classnames(styles.WarningBreak, styles.spaced)}>
          {padType === BREAK_EVENT
            ? tCommon('emptyState.none')
            : t('shiftRealHoursForm.breakCol.emptyMinute', { duration: 0 })}
        </span>
      </div>
    )
  }

  return (
    <div className={styles.BreaksRow}>
      <span>{t('shiftRealHoursForm.breakCol.break', { count: size })}</span>
      <div className={styles.BreaksCol}>
        {breaks
          .map((shiftBreak, index) => {
            const {
              plannedDuration,
              realDuration,
              shiftEventsSize,
              typeStart,
              typeEnd,
              typeDuration,
              eventType
            } = getBreaksInfosLegacy(shiftBreak, type)

            /* If we're watching planned break and plannedDuration === 0
             This break wasn't created by a shift creation but with snap-pad */
            if (type === 'planned' && plannedDuration === 0) {
              return
            }

            const isFirst = index === 0
            const caseDuration = displayRulesLegacy(
              // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
              shiftBreak?.get('id'),
              breakAsReal || true,
              shiftEventsSize,
              plannedDuration,
              shiftIsFinishedWithoutOut,
              typeStart,
              cardType,
              isFirst,
              typeDuration,
              typeEnd,
              true
            )
            const isWarningStyle =
              (type === 'real' && realDuration > plannedDuration) ||
              caseDuration

            const nullDuration =
              plannedDuration > 0 &&
              typeDuration === 0 &&
              cardType === 'real' &&
              !hasEmployeeModifyReal &&
              (breakAsReal || true) &&
              shiftEventsSize === 0 &&
              hasSnapPadBreakEnable &&
              '0 min'

            /* If nullDuration exists, the duration shouldn't exist so duration = '' */
            const rangeDates = tDates('range.shortHourMinuteDashed', {
              start: typeStart,
              end: typeEnd
            })

            const duration =
              !nullDuration &&
              (type === 'planned' ||
                shiftEventsSize === 2 ||
                hasEmployeeModifyReal ||
                isShiftValid ||
                (eventType === 'break_duration' && shiftEventsSize === 1)) &&
              t('shiftRealHoursForm.breakCol.duration', {
                duration: typeDuration,
                range: typeStart ? `(${rangeDates})` : ''
              })

            return (
              <div
                key={index}
                className={classnames(styles.spaced, {
                  [styles.WarningBreak]: isWarningStyle
                })}
              >
                {!caseDuration && nullDuration}
                {caseDuration || duration}
              </div>
            )
          })
          .toArray()}
      </div>
    </div>
  )
}
